<script setup>
import { computed } from "vue";
import { storeToRefs } from "pinia";
import { ChevronDown, ChevronRight } from "lucide-vue-next";
import { useResultsV2Store } from "@stores/survey/resultsV2/resultsV2";
import { useResultsV2CardsStore } from "@stores/survey/resultsV2/resultsV2.cards";
import { languageDirection } from "@/utils/textDirection";

const props = defineProps({
  card: { type: Object, required: true },
  readonly: { type: Boolean, default: false },
});

const store = useResultsV2Store();
const cardsStore = useResultsV2CardsStore();
const { editMode } = storeToRefs(store);
const { collapsedSectionIds, sectionIdByCardId } = storeToRefs(cardsStore);
const textDirection = computed(() => languageDirection(store.displayLanguage));

const title = computed(() => props.card.content?.title || "");

const isCollapsed = computed(() => collapsedSectionIds.value.has(props.card.id));

const sectionCardCount = computed(
  () => Object.values(sectionIdByCardId.value).filter((id) => id === props.card.id).length,
);

function toggleCollapse() {
  cardsStore.toggleSectionCollapse(props.card.id);
}

function handleInput(event) {
  cardsStore.updateCardLocal(props.card.id, {
    content: { ...props.card.content, title: event.target.value },
  });
}
</script>

<template>
  <div class="px-4" :class="editMode ? 'py-3' : 'pt-8 pb-3'">
    <div class="flex items-center gap-1.5">
      <button
        class="-ml-1.5 shrink-0 rounded p-0.5 text-muted-foreground transition-colors hover:text-foreground"
        :aria-label="isCollapsed ? 'Expand section' : 'Collapse section'"
        @click.stop="toggleCollapse">
        <ChevronRight v-if="isCollapsed" :class="editMode ? 'h-4 w-4' : 'h-5 w-5'" />
        <ChevronDown v-else :class="editMode ? 'h-4 w-4' : 'h-5 w-5'" />
      </button>
      <input
        v-if="editMode && !readonly"
        :value="title"
        :dir="textDirection"
        class="min-w-0 flex-1 border-0 bg-transparent p-0 heading-title focus:outline-none focus:ring-0"
        placeholder="Section title..."
        @input="handleInput" />
      <h2
        v-else
        :dir="textDirection"
        class="min-w-0 flex-1"
        :class="editMode ? 'heading-title' : 'text-xl font-semibold tracking-tight text-primary'">
        {{ title || "Untitled section" }}
      </h2>
      <span v-if="isCollapsed && sectionCardCount" class="shrink-0 text-2xs text-foreground-subtle">
        {{ sectionCardCount }} {{ sectionCardCount > 1 ? "cards" : "card" }}
      </span>
    </div>
    <div class="mt-2 h-px bg-border" />
  </div>
</template>
